<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import {
  fetchClans, fetchMyClan, createClan, leaveClan, kickMember,
  requestJoin, cancelJoinRequest, acceptPlea, declinePlea,
  type ClanRow, type MyClan,
} from '../game/clans-api';
import { confirmAction } from '../game/confirm';
import { toast } from '../game/toast';
import { t } from '../i18n';
import runesIcon from '../assets/fimbulwake/icons/runes.webp';
import hallArt from '../assets/fimbulwake/backgrounds/family.webp';
import runestoneArt from '../assets/fimbulwake/ui/runestone.webp';

/**
 * CLANS (QA punch-list 07.08 item 8) — deliberately primitive: a name, a tag,
 * a hall of sworn names farming saga points together. No co-op modes, no
 * shared vault. Unsworn → found a clan or swear into one from the roll;
 * sworn → your hall.
 *
 * Visual grammar matches the ward roll: carved tag-stones, a beam of tallies,
 * banner rows — not a generic admin table.
 */
const loading = ref(true);
const error = ref('');
const busy = ref(false);
const clans = ref<ClanRow[]>([]);
const mine = ref<MyClan | null>(null);

const newName = ref('');
const newTag = ref('');
const newBanner = ref(0);
const search = ref('');

/** The fixed banner rack — banner-<i>.webp, index == Clan.banner on the server
 *  (CLAN_BANNER_COUNT keeps the two sides honest). Sorted numerically: glob
 *  order is lexicographic and would put banner-10 before banner-2. */
const BANNERS = Object.entries(
  import.meta.glob<string>('../assets/fimbulwake/clans/banner-*.webp', { eager: true, import: 'default' }),
)
  .sort(([a], [b]) => Number(a.match(/banner-(\d+)/)?.[1] ?? 0) - Number(b.match(/banner-(\d+)/)?.[1] ?? 0))
  .map(([, url]) => url);
const bannerArt = (i: number): string | undefined => BANNERS[i] ?? BANNERS[0];

/** The roll, filtered by the search slot (name or tag, case-blind). */
const shownClans = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return clans.value;
  return clans.value.filter((c) => c.name.toLowerCase().includes(q) || c.tag.toLowerCase().includes(q));
});

const fmt = (n: number): string => n.toLocaleString();

/** Deterministic accent per clan tag, so every stone carries its own light. */
const ACCENTS = ['#6fb0e8', '#6fd8c0', '#d9873c', '#a892e0', '#e0938c', '#7fbf8f', '#e8d9a8', '#7fb4d8'];
function clanAccent(tag: string): string {
  let h = 0;
  for (const ch of tag) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return ACCENTS[h % ACCENTS.length];
}

/** Member points relative to the strongest, for the ledger's ember bars. */
const topMemberPoints = computed(() =>
  Math.max(1, ...(mine.value?.members.map((m) => m.points) ?? [1])));
const memberPct = (pts: number) =>
  Math.max(2, Math.round((pts / topMemberPoints.value) * 100));

async function refresh() {
  error.value = '';
  try {
    const [roll, my] = await Promise.all([fetchClans(), fetchMyClan()]);
    clans.value = roll.clans;
    mine.value = my.clan;
  } catch (e) { error.value = (e as Error).message; }
}

onMounted(async () => {
  await refresh();
  loading.value = false;
});

// The hall keeps itself current (QA 18.08 item 11): pleas answered elsewhere,
// a roster that grew, buttons armed on someone else's action — none of it
// showed until the player left and came back. A quiet 30s poll plus an
// immediate refresh when the tab regains focus; both stop with the screen.
let pollTimer = 0;
function onVisible() { if (!document.hidden) void refresh(); }
onMounted(() => {
  pollTimer = window.setInterval(() => { if (!document.hidden) void refresh(); }, 30_000);
  document.addEventListener('visibilitychange', onVisible);
});
onUnmounted(() => {
  window.clearInterval(pollTimer);
  document.removeEventListener('visibilitychange', onVisible);
});

async function onCreate() {
  if (busy.value) return;
  const name = newName.value.trim(), tag = newTag.value.trim().toUpperCase();
  if (!name || !tag) { error.value = t('clans.errorNeedNameTag'); return; }
  const ok = await confirmAction({
    kicker: t('clans.kicker'),
    title: t('clans.createTitle', { tag, name }),
    rows: [{ label: t('clans.foundingFee'), value: '200', icon: runesIcon, tone: 'bright' }],
    note: t('clans.createNote'),
    confirmLabel: t('clans.carveStone'),
  });
  if (!ok) return;
  busy.value = true; error.value = '';
  try {
    await createClan(name, tag, newBanner.value);
    toast(t('clans.createdToast', { tag, name }));
    newName.value = ''; newTag.value = ''; newBanner.value = 0;
    await refresh();
  } catch (e) { error.value = (e as Error).message; }
  finally { busy.value = false; }
}

/** Joining is a plea: the leader answers it, nothing happens on the spot. */
async function onPlea(c: ClanRow) {
  if (busy.value) return;
  const ok = await confirmAction({
    kicker: t('clans.kicker'),
    title: t('clans.joinTitle', { tag: c.tag, name: c.name }),
    note: t('clans.joinNote'),
    confirmLabel: t('clans.askToJoin'),
  });
  if (!ok) return;
  busy.value = true; error.value = '';
  try {
    const res = await requestJoin(c.id);
    clans.value = res.clans;
    toast(t('clans.pleaSentToast', { tag: c.tag, name: c.name }));
  } catch (e) { error.value = (e as Error).message; }
  finally { busy.value = false; }
}

/** Taking a plea back is free and reversible — no ceremony. */
async function onCancelPlea(c: ClanRow) {
  if (busy.value) return;
  busy.value = true; error.value = '';
  try {
    const res = await cancelJoinRequest(c.id);
    clans.value = res.clans;
    toast(t('clans.pleaCanceledToast'));
  } catch (e) { error.value = (e as Error).message; }
  finally { busy.value = false; }
}

/** Leader: let a pleader in. Reversible via kick, so no confirm dialog. */
async function onLetIn(requestId: string, username: string | null) {
  if (busy.value) return;
  busy.value = true; error.value = '';
  try {
    const res = await acceptPlea(requestId);
    mine.value = res.clan;
    toast(t('clans.letInToast', { username: username ?? t('clans.nameless') }));
  } catch (e) {
    error.value = (e as Error).message;
    await refresh(); // a dead plea (sworn elsewhere) must leave the list
  } finally { busy.value = false; }
}

async function onTurnAway(requestId: string) {
  if (busy.value) return;
  busy.value = true; error.value = '';
  try {
    const res = await declinePlea(requestId);
    mine.value = res.clan;
    toast(t('clans.turnedAwayToast'));
  } catch (e) { error.value = (e as Error).message; }
  finally { busy.value = false; }
}

async function onLeave() {
  if (busy.value || !mine.value) return;
  const solo = mine.value.members.length === 1;
  const ok = await confirmAction({
    kicker: t('clans.kicker'),
    title: t('clans.leaveTitle', { tag: mine.value.tag, name: mine.value.name }),
    note: (solo
      ? t('clans.leaveNoteSolo')
      : mine.value.isLeader
        ? t('clans.leaveNoteLeader')
        : t('clans.leaveNoteMember'))
      + t('clans.leaveNoteSuffix'),
    confirmLabel: t('clans.walkOut'),
  });
  if (!ok) return;
  busy.value = true; error.value = '';
  try {
    await leaveClan();
    toast(t('clans.leftToast'));
    await refresh();
  } catch (e) { error.value = (e as Error).message; }
  finally { busy.value = false; }
}

async function onKick(userId: string, username: string | null) {
  if (busy.value) return;
  const ok = await confirmAction({
    kicker: t('clans.kicker'),
    title: t('clans.kickTitle', { username: username ?? t('clans.thisClansman') }),
    note: t('clans.kickNote'),
    confirmLabel: t('clans.castOutConfirm'),
  });
  if (!ok) return;
  busy.value = true; error.value = '';
  try {
    await kickMember(userId);
    toast(t('clans.kickedToast', { username: username ?? t('clans.theyDefault') }));
    await refresh();
  } catch (e) { error.value = (e as Error).message; }
  finally { busy.value = false; }
}
</script>

<template>
  <div class="cl-wrap">
    <p v-if="error" class="cl-error">{{ error }}</p>
    <p v-if="loading" class="cl-loading">{{ t('clans.loading') }}</p>

    <template v-else>
      <!-- ══ MY HALL — the clan you are sworn to, as a carved plaque ══ -->
      <div v-if="mine" class="cl-hall bound-frame relative" :style="{ '--cla': clanAccent(mine.tag) }">
        <!-- the mead-hall itself: shields on the walls, the fire lit, the
             high seat waiting — the identity row sits ON the scene -->
        <div class="cl-hall-scene" aria-hidden="true">
          <img :src="hallArt" alt="" />
        </div>
        <!-- the clan's banner hangs off the hall's lintel, over the scene -->
        <img v-if="bannerArt(mine.banner)" class="cl-hall-banner" :src="bannerArt(mine.banner)" alt="" aria-hidden="true" />
        <div class="cl-hall-head">
          <span class="cl-seal" aria-hidden="true"><b>{{ mine.tag }}</b></span>
          <div class="cl-hall-id">
            <h2 class="cl-hall-name gilt-text">{{ mine.name }}</h2>
            <p class="cl-hall-sub">
              {{ t('clans.hallSub', { sworn: mine.members.length, max: mine.maxMembers }) }}
            </p>
          </div>
          <div class="cl-hall-score">
            <span class="cl-hall-pts tabular-nums">{{ fmt(mine.points) }}</span>
            <span class="cl-hall-pts-k">{{ t('clans.clanPoints') }}</span>
          </div>
        </div>

        <!-- the ledger: every sworn name, strongest first, ember bar vs the best -->
        <ul class="cl-ledger">
          <li v-for="(m, i) in mine.members" :key="m.userId" class="cl-led-row">
            <span class="cl-led-rank tabular-nums">{{ String(i + 1).padStart(2, '0') }}</span>
            <span class="cl-led-name">
              {{ m.username ?? t('clans.nameless') }}
              <i v-if="m.isLeader" class="cl-led-crown" :title="t('clans.leaderTitle')">ᛟ</i>
              <span class="cl-led-bar" aria-hidden="true"><i :style="{ width: memberPct(m.points) + '%' }"></i></span>
            </span>
            <span class="cl-led-pts tabular-nums">{{ fmt(m.points) }}</span>
            <span class="cl-led-act">
              <button
                v-if="mine.isLeader && !m.isLeader"
                class="btn-forge btn-forge--carved btn-forge--sm"
                :disabled="busy"
                @click="onKick(m.userId, m.username)"
              >{{ t('clans.castOutButton') }}</button>
            </span>
          </li>
        </ul>

        <!-- PLEAS AT THE GATE — the leader rules on who gets in (leaders only;
             everyone else receives an empty list from the server) -->
        <div v-if="mine.isLeader && mine.requests.length" class="cl-pleas">
          <div class="cl-pleas-h">
            <i aria-hidden="true">ᛒ</i>
            <span>{{ t('clans.pleasHeading') }}</span>
            <b class="tabular-nums">{{ mine.requests.length }}</b>
          </div>
          <ul class="cl-pleas-list">
            <li v-for="p in mine.requests" :key="p.id" class="cl-plea-row">
              <span class="cl-plea-name">{{ p.username ?? t('clans.nameless') }}</span>
              <span class="cl-plea-act">
                <button class="btn-forge btn-forge--rune btn-forge--sm" :disabled="busy" @click="onLetIn(p.id, p.username)">{{ t('clans.letIn') }}</button>
                <button class="btn-forge btn-forge--carved btn-forge--sm" :disabled="busy" @click="onTurnAway(p.id)">{{ t('clans.turnAway') }}</button>
              </span>
            </li>
          </ul>
        </div>

        <div class="cl-hall-foot">
          <span class="cl-hall-note">{{ t('clans.hallFootNote') }}</span>
          <button class="btn-forge btn-forge--carved" :disabled="busy" @click="onLeave">{{ t('clans.walkOut') }}</button>
        </div>
      </div>

      <!-- ══ FOUND A CLAN — the founding rite (unsworn only): your tag is carved
           into the standing stone LIVE as you type ══ -->
      <div v-else class="cl-found bound-frame relative">
        <!-- the hall you could raise, burning behind the rite -->
        <div class="cl-found-scene" aria-hidden="true"><img :src="hallArt" alt="" /></div>
        <div class="cl-stone" aria-hidden="true" :style="{ '--cla': newTag ? clanAccent(newTag) : 'transparent' }">
          <img class="cl-stone-art" :src="runestoneArt" alt="" />
          <b :class="{ 'is-blank': !newTag }">{{ (newTag || 'ᛟ').slice(0, 5) }}</b>
          <span class="cl-stone-foot">{{ newTag ? t('clans.yourMark') : t('clans.uncarved') }}</span>
        </div>
        <div class="cl-found-body">
          <div class="cl-found-kick"><i></i><span>{{ t('clans.kicker') }}</span></div>
          <h2 class="cl-found-h">{{ t('clans.foundHeading') }}</h2>
          <p class="cl-sub">
            {{ t('clans.foundSub') }}
          </p>
          <!-- the banner rack — the founder picks the cloth the hall will fly -->
          <div class="cl-banner-rack" role="radiogroup" :aria-label="t('clans.bannerLabel')">
            <span class="cl-field-k cl-banner-k">{{ t('clans.bannerLabel') }}</span>
            <div class="cl-banner-row">
              <button
                v-for="(url, i) in BANNERS" :key="i"
                type="button"
                class="cl-banner-opt"
                :class="{ 'is-picked': newBanner === i }"
                role="radio" :aria-checked="newBanner === i"
                @click="newBanner = i"
              ><img :src="url" alt="" /></button>
            </div>
          </div>
          <div class="cl-found-row">
            <label class="cl-field cl-in-name">
              <span class="cl-field-k">{{ t('clans.nameLabel') }}</span>
              <input v-model="newName" class="cl-input" maxlength="24" :placeholder="t('clans.namePlaceholder')" />
            </label>
            <label class="cl-field cl-in-tag">
              <span class="cl-field-k">{{ t('clans.tagLabel') }}</span>
              <input v-model="newTag" class="cl-input" maxlength="5" :placeholder="t('clans.tagPlaceholder')" @input="newTag = newTag.toUpperCase()" />
            </label>
            <button class="btn-forge btn-forge--rune cl-found-go" :disabled="busy" @click="onCreate">
              <span>{{ t('clans.carveStone') }}</span>
              <span class="cl-found-fee"><img :src="runesIcon" alt="" />200</span>
            </button>
          </div>
        </div>
      </div>

      <!-- ══ THE ROLL — every clan of the saga, strongest first ══ -->
      <div class="cl-roll">
        <div class="cl-roll-head">
          <h2 class="cl-h">{{ t('clans.rollHeading') }}</h2>
          <span class="cl-roll-rule" aria-hidden="true"></span>
          <label class="cl-field cl-search-field">
            <input
              v-model="search" class="cl-input cl-search" type="search"
              :placeholder="t('clans.searchPlaceholder')" :aria-label="t('clans.searchAriaLabel')"
            />
          </label>
          <span v-if="clans.length" class="cl-roll-count">{{ t('clans.rollCount', { shown: shownClans.length, total: clans.length }) }}</span>
        </div>
        <p class="cl-roll-sub">{{ t('clans.rollSub') }}</p>

        <!-- nobody has carved yet: a row of blank stones waiting for names -->
        <div v-if="!clans.length" class="cl-empty">
          <span class="cl-empty-stones" aria-hidden="true"><i v-for="k in 5" :key="k"></i></span>
          <div class="cl-empty-txt">
            <h3>{{ t('clans.emptyHeading') }}</h3>
            <p>{{ t('clans.emptyBody') }}</p>
          </div>
        </div>
        <p v-else-if="!shownClans.length" class="cl-sub cl-roll-empty">
          {{ t('clans.noMatch') }}
        </p>
        <div v-else class="cl-list">
          <article
            v-for="c in shownClans" :key="c.id"
            class="cl-band bound-frame relative"
            :class="{ 'is-mine': c.isMine, 'is-top': clans.indexOf(c) === 0 }"
            :style="{ '--cla': clanAccent(c.tag) }"
          >
            <span class="cl-band-rank tabular-nums">{{ String(clans.indexOf(c) + 1).padStart(2, '0') }}</span>
            <img v-if="bannerArt(c.banner)" class="cl-band-banner" :src="bannerArt(c.banner)" alt="" aria-hidden="true" loading="lazy" />
            <span class="cl-seal cl-seal--sm" aria-hidden="true"><b>{{ c.tag }}</b></span>
            <div class="cl-band-main">
              <h3 class="cl-band-name" :class="{ 'gilt-text': c.isMine }">{{ c.name }}</h3>
              <span class="cl-band-sworn">{{ t('clans.bandSworn', { count: c.members, max: 20 }) }}</span>
            </div>
            <div class="cl-band-score">
              <b class="tabular-nums">{{ fmt(c.points) }}</b>
              <i>{{ t('clans.points') }}</i>
            </div>
            <div class="cl-band-act">
              <template v-if="!mine && c.members < 20">
                <span v-if="c.requested" class="cl-plea-wait">
                  <i>{{ t('clans.pleaPending') }}</i>
                  <button class="btn-forge btn-forge--carved btn-forge--sm" :disabled="busy" @click="onCancelPlea(c)">{{ t('clans.cancelPlea') }}</button>
                </span>
                <button
                  v-else
                  class="btn-forge btn-forge--sm"
                  :disabled="busy"
                  @click="onPlea(c)"
                >{{ t('clans.askToJoin') }}</button>
              </template>
              <span v-else-if="c.isMine" class="cl-band-you">{{ t('clans.yourHall') }}</span>
            </div>
          </article>
        </div>

        <!-- the law of the stones, so the void under a young roll still teaches -->
        <div class="cl-law">
          <div class="cl-law-item"><b>ᛟ</b><span>{{ t('clans.law1') }}</span></div>
          <div class="cl-law-item"><b>ᛉ</b><span>{{ t('clans.law2') }}</span></div>
          <div class="cl-law-item"><b>ᛏ</b><span>{{ t('clans.law3') }}</span></div>
          <div class="cl-law-item"><b>ᛁ</b><span>{{ t('clans.law4') }}</span></div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.cl-wrap { display: grid; gap: 1.1rem; max-width: 900px; }
.cl-error { color: var(--color-error); font-size: 13px; }
.cl-loading { color: #9db0c6; font-family: var(--font-body, serif); }
.cl-h {
  font-family: var(--font-heading, serif); font-size: 18px; color: var(--color-gold, #c9a84c);
}
.cl-sub { font-family: var(--font-body, serif); font-style: italic; font-size: 12.5px; color: var(--color-text-dim, #8a8378); }

/* ── the tag seal: a hewn stone with the mark cut in, lit by the clan's color ── */
.cl-seal {
  position: relative; display: grid; place-items: center;
  width: 74px; height: 74px; flex: 0 0 auto;
  clip-path: polygon(8% 100%, 4% 22%, 16% 6%, 50% 0%, 84% 6%, 96% 22%, 92% 100%);
  background:
    radial-gradient(80% 55% at 50% 18%, color-mix(in srgb, var(--cla, #7fb4d8) 26%, transparent), transparent 70%),
    linear-gradient(180deg, #2b3342, #1a212d 62%, #141a24);
  box-shadow: inset 0 1px 0 rgba(226, 236, 248, 0.12), inset 0 0 0 1px rgba(0, 0, 0, 0.55);
}
.cl-seal b {
  font-family: var(--font-heading, serif); font-weight: 700; font-size: 19px; letter-spacing: 0.04em;
  color: color-mix(in srgb, var(--cla, #e8d9a8) 70%, #f2ecd9);
  text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.9), 0 1px 0 rgba(226, 236, 248, 0.12), 0 0 14px color-mix(in srgb, var(--cla, #e8d9a8) 40%, transparent);
}
.cl-seal--sm { width: 52px; height: 52px; }
.cl-seal--sm b { font-size: 13.5px; }

/* ── my hall ── */
.cl-hall {
  padding: 1.1rem 1.2rem 1rem;
  background: var(--surface-plank);
  background-size: var(--surface-plank-size);
  background-blend-mode: var(--surface-plank-blend);
  overflow: hidden;
}
/* the painted hall runs full-bleed behind the identity row: negative margins
   undo the plaque padding, a bottom scrim melts it into the plank */
.cl-hall-scene {
  position: relative;
  margin: -1.1rem -1.2rem 0;
  height: clamp(120px, 16vw, 190px);
}
.cl-hall-scene img {
  width: 100%; height: 100%; object-fit: cover; object-position: center 38%;
  display: block;
}
.cl-hall-scene::after {
  content: '';
  position: absolute; inset: 0;
  background:
    linear-gradient(180deg, rgba(10, 14, 22, 0.25), transparent 30%, transparent 55%, rgba(13, 17, 25, 0.94)),
    radial-gradient(70% 60% at 50% 100%, color-mix(in srgb, var(--cla, #7fb4d8) 10%, transparent), transparent 70%);
}
.cl-hall-head {
  position: relative;
  display: flex; align-items: center; gap: 16px; flex-wrap: wrap;
  margin-top: -34px;
  padding-bottom: 12px; margin-bottom: 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.55);
  box-shadow: 0 1px 0 rgba(226, 236, 248, 0.05);
}
/* basis kept low (not the ~220px a desktop layout would prefer) so seal+name
   still share one line at 320-360px before the score is forced to wrap —
   flex-grow fills the rest at every wider width, so nothing above ~380px
   renders any differently. */
.cl-hall-id { min-width: 0; flex: 1 1 130px; }
.cl-hall-name { font-family: var(--font-heading, serif); font-size: 22px; line-height: 1.1; }
.cl-hall-sub { margin-top: 4px; font-family: var(--font-body, serif); font-style: italic; font-size: 12px; color: var(--color-text-dim, #8a8378); }
.cl-hall-score { text-align: right; flex: 0 0 auto; }
.cl-hall-pts {
  display: block; font-family: var(--font-heading, serif); font-size: 30px; line-height: 1;
  color: var(--color-gold-bright, #e8d9a8);
  text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.85), 0 1px 0 rgba(226, 236, 248, 0.1);
}
.cl-hall-pts-k { font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.09em; text-transform: uppercase; color: var(--color-text-dim, #8a8378); }

/* the ledger */
.cl-ledger { display: flex; flex-direction: column; }
.cl-led-row {
  display: grid; grid-template-columns: 30px minmax(0, 1fr) auto minmax(72px, auto); gap: 12px; align-items: center;
  padding: 9px 2px; border-bottom: 1px solid rgba(0, 0, 0, 0.5);
  box-shadow: 0 1px 0 rgba(226, 236, 248, 0.05);
}
.cl-led-row:last-child { border-bottom: 0; box-shadow: none; }
.cl-led-rank { font-family: var(--font-label, sans-serif); font-size: 10px; color: var(--color-text-dim, #8a8378); }
.cl-led-name { position: relative; font-family: var(--font-heading, serif); font-size: 15px; color: var(--color-text, #e4ebf2); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding-bottom: 6px; }
.cl-led-crown { font-style: normal; color: var(--color-gold, #c9a84c); margin-left: 4px; text-shadow: 0 0 8px color-mix(in srgb, var(--color-gold, #c9a84c) 50%, transparent); }
.cl-led-bar { position: absolute; left: 0; right: 0; bottom: 0; height: 3px; background: rgba(0, 0, 0, 0.5); }
.cl-led-bar i {
  display: block; height: 100%;
  background: linear-gradient(90deg, color-mix(in srgb, var(--cla, #c9a84c) 80%, transparent), color-mix(in srgb, var(--cla, #c9a84c) 25%, transparent));
  box-shadow: 0 0 6px color-mix(in srgb, var(--cla, #c9a84c) 35%, transparent);
}
.cl-led-pts { font-family: var(--font-heading, serif); color: var(--color-gold-bright, #e8d9a8); }
.cl-led-act { display: flex; justify-content: flex-end; }
.cl-hall-foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 12px; }
.cl-hall-note { font-family: var(--font-body, serif); font-style: italic; font-size: 11px; color: var(--color-text-dim, #8a8378); }

/* ── the founding rite ── */
.cl-found {
  position: relative;
  display: flex; gap: clamp(18px, 3vw, 34px); align-items: center;
  padding: 1.4rem clamp(1.1rem, 2.5vw, 1.8rem);
  background: var(--surface-plank);
  background-size: var(--surface-plank-size);
  background-blend-mode: var(--surface-plank-blend);
  overflow: hidden;
}
/* the hall you could raise, burning low behind the rite — heavily scrimmed so
   the form stays the loudest thing on the plaque */
.cl-found-scene { position: absolute; inset: 0; pointer-events: none; }
.cl-found-scene img { width: 100%; height: 100%; object-fit: cover; object-position: center 42%; display: block; }
.cl-found-scene::after {
  content: '';
  position: absolute; inset: 0;
  background: linear-gradient(90deg, rgba(11, 15, 23, 0.66) 0%, rgba(11, 15, 23, 0.86) 42%, rgba(11, 15, 23, 0.94) 100%);
}
.cl-found > :not(.cl-found-scene) { position: relative; }
/* THE STONE — your tag carved live as you type, on real painted granite now
   (the same runestone the landing's FAQ raises). */
.cl-stone {
  position: relative; display: grid; place-items: center;
  width: clamp(120px, 14vw, 156px); aspect-ratio: 3 / 4; flex: 0 0 auto;
  clip-path: polygon(8% 100%, 4% 26%, 14% 7%, 50% 0%, 86% 7%, 96% 26%, 92% 100%);
  background: linear-gradient(180deg, #303a4b, #1e2634 58%, #161d2a);
  box-shadow:
    inset 0 2px 0 rgba(226, 236, 248, 0.12),
    inset 0 0 0 1px rgba(0, 0, 0, 0.55),
    inset 0 -26px 40px rgba(4, 8, 16, 0.5);
}
.cl-stone-art {
  position: absolute; inset: 0; width: 100%; height: 100%;
  object-fit: cover; object-position: center 26%;
  filter: brightness(0.94);
}
.cl-stone::before {
  content: ''; position: absolute; inset: 8px; pointer-events: none; z-index: 1;
  clip-path: polygon(8% 100%, 4% 26%, 14% 7%, 50% 0%, 86% 7%, 96% 26%, 92% 100%);
  box-shadow: inset 0 0 0 1px rgba(207, 232, 255, 0.14);
}
/* the clan's own light on the crown + a dark foot so the carved tag reads */
.cl-stone::after {
  content: ''; position: absolute; inset: 0; pointer-events: none; z-index: 1;
  background:
    radial-gradient(95% 55% at 50% 10%, color-mix(in srgb, var(--cla, #6fd8c0) 22%, transparent), transparent 65%),
    linear-gradient(180deg, transparent 46%, rgba(6, 10, 17, 0.78) 84%, rgba(6, 10, 17, 0.9));
}
.cl-stone b {
  position: relative; z-index: 2; font-family: var(--font-heading, serif); font-weight: 700;
  font-size: clamp(26px, 3vw, 34px); letter-spacing: 0.04em;
  color: #0c141f;
  text-shadow:
    0 2px 0 rgba(226, 236, 248, 0.2),
    0 -1px 1px rgba(0, 0, 0, 0.95),
    0 0 30px color-mix(in srgb, var(--cla, #6fd8c0) 35%, transparent);
  transition: text-shadow 300ms ease;
}
.cl-stone b.is-blank { color: #16202e; text-shadow: 0 2px 0 rgba(226, 236, 248, 0.1), 0 -1px 1px rgba(0, 0, 0, 0.9); }
.cl-stone-foot {
  position: absolute; bottom: 9%; z-index: 2;
  font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.12em; text-transform: uppercase;
  color: rgba(207, 232, 255, 0.35);
}
.cl-found-body { min-width: 0; flex: 1; }
.cl-found-kick { display: flex; align-items: center; gap: 10px; margin-bottom: 4px; }
.cl-found-kick i { width: 38px; height: 1px; background: linear-gradient(90deg, rgba(201, 168, 76, 0.8), transparent); }
.cl-found-kick span { font-family: var(--font-label, sans-serif); font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--color-gold, #c9a84c); }
.cl-found-h { font-family: var(--font-heading, serif); font-size: 22px; color: var(--color-gold-bright, #e8d9a8); margin-bottom: 4px; }
/* labelled fields — a carved caption over each chisel slot */
.cl-field { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.cl-field-k {
  font-family: var(--font-label, sans-serif); font-size: 9.5px; letter-spacing: 0.09em;
  text-transform: uppercase; color: var(--color-text-dim, #8a8378);
}
.cl-found-go { display: inline-flex; align-items: center; gap: 10px; align-self: flex-end; }
.cl-found-fee {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 12px; color: var(--color-gold-bright, #e8d9a8);
  padding-left: 10px; border-left: 1px solid rgba(226, 236, 248, 0.18);
}
.cl-found-fee img { width: 13px; height: 13px; }
/* the chisel-slot input — carved into the plank, same recipe the whole build
   uses. Local on purpose: the first cut borrowed Settings' SCOPED .st-input,
   which does not exist outside that component, and the fields rendered as
   naked browser inputs (QA screenshot, 2026-08-10). */
.cl-input {
  padding: 9px 12px;
  min-height: 44px; /* touch target floor — the 9px padding alone rendered ~38px tall */
  box-sizing: border-box;
  border: 0;
  clip-path: var(--clip-hewn);
  background: var(--sunken);
  box-shadow: var(--rim-ice-sunken);
  font-family: var(--font-body, serif); font-size: 14px;
  color: var(--color-text);
}
.cl-input::placeholder { color: var(--color-text-dim, #8a8378); font-style: italic; }
.cl-input:focus {
  outline: none;
  box-shadow:
    var(--rim-ice-sunken),
    inset 0 0 16px color-mix(in srgb, var(--color-aurora, #6fd8c0) 18%, transparent),
    inset 0 -1px 0 color-mix(in srgb, var(--color-aurora, #6fd8c0) 55%, transparent);
}
.cl-search::-webkit-search-cancel-button { -webkit-appearance: none; }

.cl-found-row { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; margin-top: 12px; }
.cl-in-name { flex: 1 1 220px; }
.cl-in-tag { flex: 0 0 96px; }
.cl-in-tag .cl-input { text-transform: uppercase; }

/* ── the roll ── */
/* wrap is load-bearing: the search field's own 150px floor plus the heading
   and count labels sum past the available width below ~370px — without wrap
   that is a hard document overflow, not just a squeeze. */
.cl-roll-head { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; flex-wrap: wrap; row-gap: 8px; }
.cl-roll-rule {
  flex: 1; height: 7px; align-self: center;
  background:
    radial-gradient(4px 4px at 4px 50%, rgba(232, 217, 168, 0.8), transparent 70%),
    linear-gradient(90deg, rgba(232, 217, 168, 0.4), rgba(232, 217, 168, 0.08) 60%, transparent);
}
.cl-roll-count { font-family: var(--font-label, sans-serif); font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--color-text-dim, #8a8378); }
.cl-roll-empty { padding: 8px 2px; }
.cl-list { display: flex; flex-direction: column; gap: 9px; }
.cl-band {
  display: grid; grid-template-columns: 32px auto auto minmax(0, 1fr) auto minmax(84px, auto);
  gap: 14px; align-items: center;
  padding: 9px 14px 9px 10px;
  background: var(--surface-plank);
  background-size: var(--surface-plank-size);
  background-blend-mode: var(--surface-plank-blend);
}
.cl-band.is-top { box-shadow: inset 0 1px 0 rgba(232, 217, 168, 0.25), inset 0 0 0 1.5px rgba(201, 169, 92, 0.45), inset 0 0 26px rgba(201, 169, 92, 0.10); }
.cl-band.is-mine { box-shadow: inset 0 0 0 1.5px color-mix(in srgb, var(--cla, #c9a84c) 45%, transparent), inset 0 0 24px color-mix(in srgb, var(--cla, #c9a84c) 8%, transparent); }
.cl-band-rank {
  font-family: var(--font-heading, serif); font-size: 17px; text-align: center;
  color: rgba(207, 232, 255, 0.4); text-shadow: 0 1px 0 rgba(0, 0, 0, 0.8);
}
.cl-band.is-top .cl-band-rank { color: var(--color-gold-bright, #e8d9a8); }
.cl-band-main { min-width: 0; }
.cl-band-name { font-family: var(--font-heading, serif); font-size: 16.5px; color: var(--color-text, #e4ebf2); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cl-band-sworn { font-family: var(--font-label, sans-serif); font-size: 9.5px; letter-spacing: 0.07em; text-transform: uppercase; color: var(--color-text-dim, #8a8378); }
.cl-band-score { text-align: right; }
.cl-band-score b { display: block; font-family: var(--font-heading, serif); font-size: 17px; color: var(--color-gold-bright, #e8d9a8); }
.cl-band-score i { font-style: normal; font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--color-text-dim, #8a8378); }
.cl-band-act { display: flex; justify-content: flex-end; min-width: 0; }
.cl-band-you { font-family: var(--font-label, sans-serif); font-size: 10px; letter-spacing: 0.07em; text-transform: uppercase; color: var(--color-gold, #c9a84c); }

.cl-roll-sub {
  margin: 2px 0 10px;
  font-family: var(--font-body, serif); font-style: italic; font-size: 12.5px;
  color: var(--color-text-dim, #8a8378);
}

/* the empty roll: five blank stones waiting for names (Districts' beyond-the-ice grammar) */
.cl-empty { display: flex; align-items: center; gap: 20px; padding: 18px 6px 6px; flex-wrap: wrap; }
.cl-empty-stones { display: flex; align-items: flex-end; gap: 9px; }
.cl-empty-stones i {
  width: 30px; height: 42px; display: block;
  clip-path: polygon(8% 100%, 4% 26%, 14% 7%, 50% 0%, 86% 7%, 96% 26%, 92% 100%);
  background: linear-gradient(180deg, #232c3a, #161d2a);
  box-shadow: inset 0 1px 0 rgba(226, 236, 248, 0.08), inset 0 0 0 1px rgba(0, 0, 0, 0.5);
}
.cl-empty-stones i:nth-child(2) { height: 50px; }
.cl-empty-stones i:nth-child(3) { height: 58px; }
.cl-empty-stones i:nth-child(4) { height: 48px; }
.cl-empty-stones i:nth-child(5) { height: 40px; }
.cl-empty-txt h3 { font-family: var(--font-heading, serif); font-size: 16px; color: var(--color-text, #e4ebf2); }
.cl-empty-txt p { font-family: var(--font-body, serif); font-style: italic; font-size: 12.5px; color: var(--color-text-dim, #8a8378); margin-top: 2px; }

/* the law of the stones — four short rules on one quiet strip */
.cl-law {
  display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px;
  margin-top: 18px; padding-top: 14px;
  border-top: 1px solid rgba(0, 0, 0, 0.55);
  box-shadow: 0 -1px 0 rgba(226, 236, 248, 0.05) inset;
}
.cl-law-item { display: flex; gap: 9px; align-items: flex-start; }
.cl-law-item b {
  font-family: var(--font-heading, serif); font-weight: 700; font-size: 17px; line-height: 1;
  color: transparent; -webkit-text-stroke: 1px rgba(150, 200, 236, 0.5);
}
.cl-law-item span { font-family: var(--font-body, serif); font-size: 11.5px; line-height: 1.45; color: var(--color-text-dim, #8a8378); }
.cl-search-field { flex: 0 1 240px; min-width: 150px; }
.cl-search { width: 100%; padding: 7px 11px; font-size: 13px; }

/* ── the banners — real cloth, cut out, hung where the clan lives ───────── */
/* the founder's rack: one thumb per fixed banner art */
.cl-banner-rack { margin-top: 12px; }
.cl-banner-k { display: block; margin-bottom: 6px; }
.cl-banner-row { display: flex; flex-wrap: wrap; gap: 8px; }
.cl-banner-opt {
  width: 46px; height: 64px; padding: 4px;
  border: 1px solid rgba(226, 236, 248, 0.14);
  background: rgba(0, 0, 0, 0.35);
  cursor: pointer;
  transition: border-color 130ms ease, box-shadow 130ms ease;
}
.cl-banner-opt img { width: 100%; height: 100%; object-fit: contain; filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.6)); }
.cl-banner-opt:hover { border-color: rgba(226, 236, 248, 0.35); }
.cl-banner-opt.is-picked {
  border-color: color-mix(in srgb, var(--color-gold, #c8a24c) 75%, transparent);
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--color-gold, #c8a24c) 45%, transparent),
              0 0 14px color-mix(in srgb, var(--color-gold, #c8a24c) 25%, transparent);
}
/* the hall flies its banner off the lintel, over the scene's right shoulder */
.cl-hall-banner {
  position: absolute; top: 4px; right: 22px; z-index: 1;
  height: 132px; width: auto;
  filter: drop-shadow(0 8px 14px rgba(0, 0, 0, 0.65));
  pointer-events: none;
}
/* the roll: a slim cloth beside each tag-stone */
.cl-band-banner {
  height: 56px; width: auto; justify-self: center;
  filter: drop-shadow(0 3px 5px rgba(0, 0, 0, 0.6));
}

/* ── pleas — asking in, and the leader's gate ───────────────────────────── */
.cl-plea-wait { display: inline-flex; align-items: center; gap: 10px; }
.cl-plea-wait i {
  font-family: var(--font-label, sans-serif); font-style: normal;
  font-size: 10px; text-transform: uppercase; letter-spacing: 0.07em;
  color: var(--color-gold, #c8a24c); white-space: nowrap;
}
.cl-pleas { margin-top: 4px; padding: 10px 12px; background: rgba(0, 0, 0, 0.28); border: 1px solid rgba(201, 169, 92, 0.28); }
.cl-pleas-h {
  display: flex; align-items: center; gap: 8px; margin-bottom: 8px;
  font-family: var(--font-label, sans-serif); font-size: 11px;
  text-transform: uppercase; letter-spacing: 0.08em; color: var(--color-gold, #c8a24c);
}
.cl-pleas-h i { font-style: normal; opacity: 0.8; }
.cl-pleas-h b { padding: 0 7px; background: color-mix(in srgb, var(--color-gold, #c8a24c) 18%, transparent); }
.cl-pleas-list { display: grid; gap: 6px; }
.cl-plea-row {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 6px 8px; background: rgba(0, 0, 0, 0.25);
}
.cl-plea-name { font-family: var(--font-body, serif); font-size: 13.5px; color: var(--color-text, #dbe5ef); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cl-plea-act { display: inline-flex; gap: 8px; flex-shrink: 0; }
@media (max-width: 480px) {
  .cl-plea-row { flex-wrap: wrap; }
  .cl-plea-act { width: 100%; justify-content: stretch; }
  .cl-plea-act .btn-forge { flex: 1; }
}

@media (max-width: 640px) {
  .cl-band { grid-template-columns: 26px auto minmax(0, 1fr) auto; }
  /* the cloth needs height to read — in the tight roll rows it only crowds
     them, but the hall keeps its banner at a size the phone can afford */
  .cl-band-banner { display: none; }
  .cl-hall-banner { height: 84px; top: 6px; right: 10px; }
  /* the name is the row's whole point: the four columns left it ~87px, so even
     'Frostborn' ellipsised. Give it back the width the rank digits and the
     seal's inset were taking. */
  .cl-band { gap: 10px; padding-left: 6px; }
  .cl-band-rank { font-size: 13px; }
  .cl-seal--sm { width: 44px; height: 44px; }
  .cl-seal--sm b { font-size: 11.5px; }
  .cl-band-name { font-size: 15px; }
  .cl-band-score b { font-size: 15px; }
  .cl-band-act { grid-column: 1 / -1; justify-content: stretch; }
  .cl-band-act .btn-forge { width: 100%; }
  .cl-found { flex-direction: column; align-items: stretch; }
  .cl-stone { display: none; }
  .cl-law { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
  .cl-found-go { align-self: stretch; justify-content: center; }
  .cl-led-row { grid-template-columns: 24px minmax(0, 1fr) auto; }
  .cl-led-act { grid-column: 1 / -1; }
  .cl-led-act:empty { display: none; }
}

/* Under ~400px even the reclaimed width cannot hold 'The Hanged Ones' on one
   line. A clan's name is its identity — let it take a second line rather than
   ellipsising it away. */
@media (max-width: 400px) {
  .cl-band-name {
    font-size: 14.5px; line-height: 1.12;
    white-space: normal;
    display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; line-clamp: 2;
    overflow: hidden;
  }
}
</style>
